import { Button } from "#components/ui/button"
import { Calendar } from "#components/ui/calendar"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "#components/ui/drawer"
import { Popover, PopoverContent, PopoverTrigger } from "#components/ui/popover"
import { useDesktop } from "#hooks/use-desktop"
import { useI18n } from "#hooks/use-i18n"
import { getDateLocale } from "#lib/formatters"
import { cn } from "#lib/utils"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import type { DateRange } from "react-day-picker"

interface DateRangePickerProps {
  startDate?: string
  endDate?: string
  onDateRangeChange: (startDate: string | undefined, endDate: string | undefined) => void
  className?: string
}

type QuickAction = "current-month" | "previous-month" | "custom"

const normalizeDate = (date: Date): Date => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

const getMonthBounds = (year: number, month: number) => ({
  start: new Date(year, month, 1),
  end: new Date(year, month + 1, 0, 23, 59, 59, 999),
})

export function DateRangePicker({
  startDate,
  endDate,
  onDateRangeChange,
  className,
}: DateRangePickerProps) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [selectedAction, setSelectedAction] = useState<QuickAction>("current-month")
  const [localFrom, setLocalFrom] = useState<Date | undefined>(undefined)
  const [localTo, setLocalTo] = useState<Date | undefined>(undefined)

  useEffect(() => {
    if (open) {
      setLocalFrom(startDate ? new Date(startDate) : undefined)
      setLocalTo(endDate ? new Date(endDate) : undefined)
    }
  }, [open, startDate, endDate])

  useEffect(() => {
    if (!startDate || !endDate) {
      setSelectedAction("current-month")
      return
    }

    const now = new Date()
    const current = getMonthBounds(now.getFullYear(), now.getMonth())
    const previous = getMonthBounds(now.getFullYear(), now.getMonth() - 1)
    const start = new Date(startDate)
    const end = new Date(endDate)

    if (start.getTime() === current.start.getTime() && end.getTime() === current.end.getTime()) {
      setSelectedAction("current-month")
    } else if (
      start.getTime() === previous.start.getTime() &&
      end.getTime() === previous.end.getTime()
    ) {
      setSelectedAction("previous-month")
    } else {
      setSelectedAction("custom")
    }
  }, [startDate, endDate])

  const dateRange = useMemo<DateRange | undefined>(() => {
    if (!localFrom) return undefined
    return { from: localFrom, to: localTo }
  }, [localFrom, localTo])

  const handleQuickAction = useCallback(
    (action: QuickAction) => {
      if (action === "custom") {
        setSelectedAction("custom")
        return
      }

      const now = new Date()
      const bounds =
        action === "current-month"
          ? getMonthBounds(now.getFullYear(), now.getMonth())
          : getMonthBounds(now.getFullYear(), now.getMonth() - 1)

      onDateRangeChange(bounds.start.toISOString(), bounds.end.toISOString())
      setSelectedAction(action)
      setOpen(false)
    },
    [onDateRangeChange]
  )

  const handleCalendarSelect = useCallback(
    (range: DateRange | undefined) => {
      if (!range?.from) {
        setLocalFrom(undefined)
        setLocalTo(undefined)
        setSelectedAction("custom")
        return
      }

      const rangeFrom = normalizeDate(range.from)
      const rangeTo = range.to ? normalizeDate(range.to) : null
      const currentTo = localTo ? normalizeDate(localTo) : null

      if (localFrom && localTo) {
        const isNewTo = rangeTo && currentTo && rangeTo.getTime() !== currentTo.getTime()
        setLocalFrom(isNewTo ? rangeTo : rangeFrom)
        setLocalTo(undefined)
        setSelectedAction("custom")
        return
      }

      if (localFrom && !localTo) {
        const endDate = rangeTo || rangeFrom
        if (endDate < localFrom) {
          setLocalTo(localFrom)
          setLocalFrom(endDate)
        } else {
          setLocalTo(endDate)
        }
        setSelectedAction("custom")
        return
      }

      setLocalFrom(rangeFrom)
      setLocalTo(undefined)
      setSelectedAction("custom")
    },
    [localFrom, localTo]
  )

  const handleValidate = useCallback(() => {
    if (!localFrom) {
      onDateRangeChange(undefined, undefined)
    } else if (localFrom && localTo) {
      const start = normalizeDate(localFrom)
      const end = new Date(localTo)
      end.setHours(23, 59, 59, 999)
      onDateRangeChange(start.toISOString(), end.toISOString())
    } else {
      return
    }
    setOpen(false)
  }, [localFrom, localTo, onDateRangeChange])

  const handleCancel = useCallback(() => {
    setLocalFrom(startDate ? new Date(startDate) : undefined)
    setLocalTo(endDate ? new Date(endDate) : undefined)
    setOpen(false)
  }, [startDate, endDate])

  const displayValue = useMemo(() => {
    if (!startDate) return t("components.dateRange.selectRange")

    const dateLocale = getDateLocale()

    try {
      const start = new Date(startDate)
      if (isNaN(start.getTime())) return t("components.dateRange.selectRange")

      if (!endDate) return `${format(start, "MMM d, yyyy", { locale: dateLocale })} - ...`

      const end = new Date(endDate)
      if (isNaN(end.getTime()))
        return `${format(start, "MMM d, yyyy", { locale: dateLocale })} - ...`

      const startOfMonth = new Date(start.getFullYear(), start.getMonth(), 1)
      const endOfMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0, 23, 59, 59, 999)

      if (
        start.getTime() === startOfMonth.getTime() &&
        end.getTime() === endOfMonth.getTime() &&
        start.getMonth() === end.getMonth()
      ) {
        return format(start, "MMMM yyyy", { locale: dateLocale })
      }

      return `${format(start, "MMM d", { locale: dateLocale })} - ${format(end, "MMM d, yyyy", { locale: dateLocale })}`
    } catch {
      return t("components.dateRange.selectRange")
    }
  }, [startDate, endDate, t])

  const canValidate = localFrom && localTo
  const isDesktop = useDesktop()

  const triggerButton = (
    <Button
      variant="outline"
      className={cn(
        "w-[180px] justify-start text-left font-normal shadow-sm dark:shadow-none dark:border-border/50",
        !startDate && "text-muted-foreground",
        className
      )}
      data-tourid="date-range-picker"
    >
      <CalendarIcon className="mr-2 h-4 w-4" />
      {displayValue}
    </Button>
  )

  const content = (
    <>
      <div className={cn("p-3 border-b", !isDesktop && "px-4")}>
        <div className="space-y-1">
          <Button
            variant={selectedAction === "current-month" ? "default" : "ghost"}
            className="w-full justify-start text-sm"
            onClick={() => handleQuickAction("current-month")}
          >
            {t("components.dateRange.currentMonth")}
          </Button>
          <Button
            variant={selectedAction === "previous-month" ? "default" : "ghost"}
            className="w-full justify-start text-sm"
            onClick={() => handleQuickAction("previous-month")}
          >
            {t("components.dateRange.previousMonth")}
          </Button>
          <Button
            variant={selectedAction === "custom" ? "default" : "ghost"}
            className="w-full justify-start text-sm"
            onClick={() => setSelectedAction("custom")}
          >
            {t("components.dateRange.customRange")}
          </Button>
        </div>
      </div>
      <div className={cn(!isDesktop && "w-full")}>
        <Calendar
          mode="range"
          defaultMonth={dateRange?.from || localFrom}
          selected={dateRange}
          onSelect={handleCalendarSelect}
          numberOfMonths={isDesktop ? 2 : 1}
          initialFocus
          className={cn(!isDesktop && "w-full")}
        />
      </div>
      <div className={cn("p-3 border-t flex gap-2 justify-end", !isDesktop && "px-4 pb-4")}>
        <Button variant="outline" size="sm" onClick={handleCancel}>
          {t("components.dateRange.cancel")}
        </Button>
        <Button variant="default" size="sm" onClick={handleValidate} disabled={!canValidate}>
          {t("components.dateRange.validate")}
        </Button>
      </div>
    </>
  )

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          {content}
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
      <DrawerContent className="max-h-[90vh]">
        <DrawerHeader>
          <DrawerTitle>{t("components.dateRange.selectRange")}</DrawerTitle>
          <DrawerDescription>{t("components.dateRange.description")}</DrawerDescription>
        </DrawerHeader>
        <div className="overflow-y-auto">{content}</div>
      </DrawerContent>
    </Drawer>
  )
}
